import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  MenuItem,
  Button,
  Tooltip,
  Typography,
} from "@mui/material";
import { StatusCard } from "@/components/dashboard/StatusCard";
import type { InstrumentSummary } from "./InstrumentSearch";

const orderTicketSchema = z.object({
  side: z.enum(["BUY", "SELL"]),
  orderType: z.enum(["MARKET", "LIMIT"]),
  quantity: z.coerce.number().int().positive(),
  price: z.coerce.number().nonnegative().optional(),
  product: z.enum(["INTRADAY", "DELIVERY"]),
  validity: z.enum(["DAY", "IOC"]),
});

export type OrderTicketValues = z.infer<typeof orderTicketSchema>;

interface OrderTicketProps {
  instrument: InstrumentSummary | null;
  onPreview: (values: OrderTicketValues) => void;
}

/**
 * Display-only order entry form. The Submit button is permanently
 * disabled - there is no code path anywhere in this component (or
 * anywhere in this sprint) that calls an order-placement endpoint. Only
 * "Preview" is wired, and it only populates `OrderPreview` with the
 * form's own already-entered values - it never submits anything.
 */
export function OrderTicket({ instrument, onPreview }: OrderTicketProps): React.JSX.Element {
  const { control, handleSubmit, watch } = useForm<OrderTicketValues>({
    resolver: zodResolver(orderTicketSchema),
    defaultValues: {
      side: "BUY",
      orderType: "MARKET",
      quantity: 1,
      product: "INTRADAY",
      validity: "DAY",
    },
  });
  const side = watch("side");

  return (
    <StatusCard title="Order Ticket">
      <Box component="form" onSubmit={handleSubmit(onPreview)}>
        <Stack spacing={2}>
          <Controller
            name="side"
            control={control}
            render={({ field }) => (
              <ToggleButtonGroup
                exclusive
                value={field.value}
                onChange={(_event, value) => value && field.onChange(value)}
                color={side === "BUY" ? "success" : "error"}
                fullWidth
              >
                <ToggleButton value="BUY">Buy</ToggleButton>
                <ToggleButton value="SELL">Sell</ToggleButton>
              </ToggleButtonGroup>
            )}
          />
          <TextField label="Instrument" value={instrument?.trading_symbol ?? ""} disabled fullWidth size="small" />
          <TextField label="Exchange" value={instrument?.exchange_code ?? ""} disabled fullWidth size="small" />
          <Controller
            name="orderType"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label="Order Type" fullWidth size="small">
                <MenuItem value="MARKET">Market</MenuItem>
                <MenuItem value="LIMIT">Limit</MenuItem>
              </TextField>
            )}
          />
          <Controller
            name="quantity"
            control={control}
            render={({ field }) => (
              <TextField {...field} type="number" label="Quantity" fullWidth size="small" />
            )}
          />
          <Controller
            name="price"
            control={control}
            render={({ field }) => (
              <TextField {...field} type="number" label="Price" fullWidth size="small" />
            )}
          />
          <Controller
            name="product"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label="Product" fullWidth size="small">
                <MenuItem value="INTRADAY">Intraday</MenuItem>
                <MenuItem value="DELIVERY">Delivery</MenuItem>
              </TextField>
            )}
          />
          <Controller
            name="validity"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label="Validity" fullWidth size="small">
                <MenuItem value="DAY">Day</MenuItem>
                <MenuItem value="IOC">IOC</MenuItem>
              </TextField>
            )}
          />
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="outlined" disabled={!instrument} fullWidth>
              Preview
            </Button>
            <Tooltip title="Order submission is disabled in this workspace">
              <span style={{ width: "100%" }}>
                <Button variant="contained" disabled fullWidth>
                  Submit
                </Button>
              </span>
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Order submission is disabled — this workspace is preview-only.
          </Typography>
        </Stack>
      </Box>
    </StatusCard>
  );
}
